import { useEffect, useLayoutEffect, useRef, type RefObject } from 'react';
import { PHONE_READER } from '@constants/timing';

const editable = (el: EventTarget | null) =>
  el instanceof HTMLElement && (el.isContentEditable || el instanceof HTMLTextAreaElement || el instanceof HTMLSelectElement || (el instanceof HTMLInputElement && !['button', 'checkbox', 'radio', 'submit', 'reset', 'file', 'range', 'color'].includes(el.type)));

/**
 * A phone Reader over OTG in keyboard mode ("keyboard wedge"): it types a tag's EPC and Enter in a burst. While
 * `enabled`, every such burst (keys at most PHONE_READER.maxGapMs apart, at least PHONE_READER.minChars, then Enter)
 * is handed to `onRead` in capitals — a continuous stream works (a sweep), nothing has to be clicked between reads.
 * The keys are taken when nothing editable has the focus (a button, the page) or when `own` (the field's own input)
 * has it; another field keeps its keys (the cash, the code), so the reader never writes into it from here. The Enter
 * of a burst is consumed (no form is submitted by it).
 */
export function usePhoneReader(onRead: (code: string) => void, opts: { enabled: boolean; own?: RefObject<HTMLInputElement | null> }) {
  const cb = useRef(onRead);
  useLayoutEffect(() => {
    cb.current = onRead;
  });
  const { enabled, own } = opts;
  useEffect(() => {
    if (!enabled) return;
    let buf = '';
    let last = 0;
    let first = 0;
    const onKey = (e: KeyboardEvent) => {
      if (e.ctrlKey || e.metaKey || e.altKey) return;
      const t = e.target;
      const mine = !!own?.current && t === own.current;
      // another field keeps its keys; so does a dialog the reader's field is not in (its radios, its buttons)
      const dialog = t instanceof Element ? t.closest('[role="dialog"]') : null;
      if ((editable(t) && !mine) || (dialog && !(own?.current && dialog.contains(own.current)))) {
        buf = '';
        return;
      }
      const now = performance.now();
      if (e.key === 'Enter') {
        const fast = buf.length >= PHONE_READER.minChars && (now - first) / buf.length <= PHONE_READER.maxGapMs;
        if (fast) {
          e.preventDefault();
          e.stopPropagation();
          cb.current(buf.toUpperCase());
        }
        buf = '';
        return;
      }
      if (e.key.length !== 1 || !/[0-9a-z]/i.test(e.key)) return;
      if (!buf || now - last > PHONE_READER.maxGapMs) {
        buf = '';
        first = now;
      }
      buf += e.key;
      last = now;
      // keys that land on no field would do nothing anyway; taking them keeps page shortcuts out of the way
      if (!mine) e.preventDefault();
    };
    window.addEventListener('keydown', onKey, true);
    return () => window.removeEventListener('keydown', onKey, true);
  }, [enabled, own]);
}
